import React, { useState } from 'react';
import { connect } from 'react-redux';

import useMartyContext from 'hooks/useMartyContext';
import {
  modifyQuantityEvent,
  removeFromCartEvent
} from 'events/cart';
import {
  cartError,
  changeQuantity,
  fetchCartItems
} from 'actions/cart';
import { translateCartError } from 'apis/mafia';
import JanusPixel from 'components/common/JanusPixel';
import { withErrorBoundary } from 'components/common/MartyErrorBoundary';

const maxQuantity = 10;

export const makeOptions = (onHand, currentVal) => {
  /* if we have a ton on hand, just set to 10 */
  if (onHand > maxQuantity) {
    onHand = maxQuantity;
  }

  const options = [];

  for (let i = 1; i <= onHand; i++) {
    options.push(<option key={i} value={i}>{i}</option>);
  }

  if (currentVal > onHand) {
    options.push(<option disabled key={currentVal} value={currentVal}>{currentVal}</option>);
  }

  return options;
};

export const CartQuantityDropdown = ({
  item,
  changeQuantity,
  cartError,
  fetchCartItems,
  forceRemoveButton,
  onChangeQuantityCb,
  onChangeQuantityCbDone,
  removeFromCartEvent,
  modifyQuantityEvent
}) => {
  const [isDeleting, setDeleting] = useState(false);

  const { testId, marketplace: { cart: { allowAdjustQuantity } } } = useMartyContext();

  const {
    egc,
    asin,
    quantity,
    stock,
    stockId,
    styleId,
    onHand,
    cartItemId
  } = item;

  const onChangeQuantity = ({ currentTarget }) => {

    const {
      dataset: { asin, cartitemid, isEgc },
      value: quantity
    } = currentTarget;

    onChangeQuantityCb(asin);

    if (+quantity === 0) {
      setDeleting(true);
      removeFromCartEvent(asin);
    } else {
      modifyQuantityEvent(quantity, asin);
    }

    const itemData = { asin, quantity };

    if (isEgc === 'true' && cartitemid) {
      itemData.itemType = 'egc';
      itemData.cartItemId = cartitemid;
    }

    changeQuantity({ 'items': [itemData] }).then(response => {
      !!+quantity && onChangeQuantityCbDone();

      // TODO: remove this logic once https://github01.zappos.net/mweb/marty/issues/6291 is addressed on the mafia side
      if (+quantity === 0 && isEgc === 'true') {
        fetchCartItems();
      }

      const error = translateCartError(response);

      if (error) {
        cartError(error);
      }
    });
  };

  const makeJanusPixel = () => {
    const queryParams = {
      teen: styleId,
      child: stockId,
      widget: 'DeleteCart'
    };

    return <JanusPixel queryParams={queryParams} />;
  };

  // if item is an egc, item is out of stock, item does not allow quantity adjustments, or we just want to force the remove button instead of the dropdown
  if (egc || !stock || !allowAdjustQuantity || forceRemoveButton) {
    return (
      <button
        type="button"
        data-asin={asin}
        data-is-egc={egc}
        data-cartitemid={cartItemId}
        value={0}
        data-test-id={testId('removeButton')}
        onClick={onChangeQuantity}>Remove{forceRemoveButton && ' This Size'}</button>
    );
  }

  return (
    <>
      <label id={`quantity-${asin}`} htmlFor="quantity" data-test-id={testId('quantityDropdownLabel')}>Quantity</label>
      <select
        id={`quantity-${asin}`}
        data-asin={asin}
        value={quantity}
        onChange={onChangeQuantity}
        data-test-id={testId('quantityDropdown')}
        name="quantity">
        <option value={0}>Remove</option>
        { makeOptions(onHand, quantity) }
      </select>
      { isDeleting && makeJanusPixel(item) }
    </>
  );
};

export function mapStateToProps(state) {
  const { cart } = state;
  return {
    cart
  };
}

const ConnectedCartQuantityDropdown = connect(mapStateToProps, {
  cartError,
  changeQuantity,
  fetchCartItems,
  modifyQuantityEvent,
  removeFromCartEvent
})(CartQuantityDropdown);

export default withErrorBoundary('CartQuantityDropdown', ConnectedCartQuantityDropdown);
